import { LocalDate } from "@js-joda/core";
import type { CompanyBankAccountType } from "model/Company";
import type {
  SalaryPayoutPreviewResponse,
  SalaryPayoutPreviewWorker,
  SalaryPayoutWorker,
} from "model/Salary";
import { formatNumber } from "utils/utils";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type SalaryPayoutCreateErrorTranslationKey =
  | "Salary.PaymentCreateErrorCompanyNotFound"
  | "Salary.PaymentCreateErrorMissingBankAccountType"
  | "Salary.PaymentCreateErrorMissingRequiredSettings"
  | "Salary.PaymentCreateErrorNoValidWorkers"
  | "Salary.PaymentCreateErrorNoPayouts"
  | "Salary.PaymentCreateErrorNoPaymentMethod"
  | "Alerts.SomethingWentWrong";

export interface BuildSalaryPayoutPanelViewModelParams {
  readonly bankAccountType: CompanyBankAccountType;
  readonly bankAccountValue: string;
  readonly bankAgreementId: string;
  readonly currentDate: LocalDate;
  readonly totalNetPayoutAmount: number;
  readonly translate: TranslateFn;
}

export interface SalaryPayoutPanelViewModel {
  readonly bankAccountTypeLabel: string;
  readonly bankAccountValue: string;
  readonly bankAgreementId: string;
  readonly currentMonthLabel: string;
  readonly totalNetPayoutAmount: string;
}

export interface SalaryPayoutExcludedWorkerRowViewModel {
  readonly fullName: string;
  readonly tagLabel: string;
  readonly workerId: number;
}

export interface SalaryPayoutIncludedWorkerRowViewModel {
  readonly fullName: string;
  readonly payoutAmount: string;
  readonly workerId: number;
}

export interface SalaryPayoutExcludedSectionViewModel {
  readonly id: "missingPaymentMethod" | "missingPayout";
  readonly rows: readonly SalaryPayoutExcludedWorkerRowViewModel[];
  readonly subtitle: string;
  readonly title: string;
}

export interface SalaryPayoutIncludedSectionViewModel {
  readonly emptyText: string;
  readonly rows: readonly SalaryPayoutIncludedWorkerRowViewModel[];
  readonly subtitle: string;
  readonly title: string;
}

export interface SalaryPayoutSectionsViewModel {
  readonly excludedSections: readonly SalaryPayoutExcludedSectionViewModel[];
  readonly includedSection: SalaryPayoutIncludedSectionViewModel;
}

export interface BuildSalaryPayoutSectionsViewModelParams {
  readonly previewData: SalaryPayoutPreviewResponse;
  readonly totalNetPayoutAmount: number;
  readonly translate: TranslateFn;
}

interface GetSalaryPayoutBankAccountTypeLabelParams {
  readonly bankAccountType: CompanyBankAccountType;
  readonly translate: TranslateFn;
}

interface GetSalaryPayoutMonthLabelParams {
  readonly currentDate: LocalDate;
  readonly translate: TranslateFn;
}

interface BuildExcludedSectionParams {
  readonly id: "missingPaymentMethod" | "missingPayout";
  readonly rows: readonly SalaryPayoutExcludedWorkerRowViewModel[];
  readonly translate: TranslateFn;
}

function toExcludedWorkerRows({
  tagLabel,
  workers,
}: {
  readonly tagLabel: string;
  readonly workers: readonly SalaryPayoutWorker[];
}): readonly SalaryPayoutExcludedWorkerRowViewModel[] {
  return workers.map((worker) => ({
    fullName: worker.fullName,
    tagLabel,
    workerId: worker.workerId,
  }));
}

function toIncludedWorkerRows({
  workers,
}: {
  readonly workers: readonly SalaryPayoutPreviewWorker[];
}): readonly SalaryPayoutIncludedWorkerRowViewModel[] {
  return workers.map((worker) => ({
    fullName: worker.fullName,
    payoutAmount: formatSalaryPayoutCurrency(worker.payoutAmount),
    workerId: worker.workerId,
  }));
}

function buildExcludedSection({
  id,
  rows,
  translate,
}: BuildExcludedSectionParams): SalaryPayoutExcludedSectionViewModel {
  if (id === "missingPaymentMethod") {
    return {
      id,
      rows,
      subtitle: translate("Salary.PaymentMissingPaymentMethodInfo"),
      title: translate("Salary.PaymentMissingPaymentMethodAccordionTitle", {
        count: rows.length,
      }),
    };
  }

  return {
    id,
    rows,
    subtitle: translate("Salary.PaymentMissingPayoutInfo"),
    title: translate("Salary.PaymentMissingPayoutAccordionTitle", {
      count: rows.length,
    }),
  };
}

function getSalaryPayoutMonthLabel({
  currentDate,
  translate,
}: GetSalaryPayoutMonthLabelParams): string {
  const monthKeys = [
    "General.January",
    "General.February",
    "General.March",
    "General.April",
    "General.May",
    "General.June",
    "General.July",
    "General.August",
    "General.September",
    "General.October",
    "General.November",
    "General.December",
  ];

  return `${translate(monthKeys[currentDate.monthValue() - 1])} ${currentDate.year()}`;
}

export function formatSalaryPayoutCurrency(value: number): string {
  return `${formatNumber(value.toFixed(2))} SEK`;
}

export function getSalaryPayoutBankAccountTypeLabel({
  bankAccountType,
  translate,
}: GetSalaryPayoutBankAccountTypeLabelParams): string {
  if (bankAccountType === "InternationalBankAccount") {
    return translate("System.BankAccountTypeOptionInternationalBankAccount");
  }

  return translate("System.BankAccountTypeOptionBankAccount");
}

export function buildSalaryPayoutPanelViewModel({
  bankAccountType,
  bankAccountValue,
  bankAgreementId,
  currentDate,
  totalNetPayoutAmount,
  translate,
}: BuildSalaryPayoutPanelViewModelParams): SalaryPayoutPanelViewModel {
  return {
    bankAccountTypeLabel: getSalaryPayoutBankAccountTypeLabel({
      bankAccountType,
      translate,
    }),
    bankAccountValue:
      bankAccountValue.trim().length > 0
        ? bankAccountValue.trim()
        : translate("Salary.PaymentBankProfilePlaceholder"),
    bankAgreementId:
      bankAgreementId.trim().length > 0
        ? bankAgreementId.trim()
        : translate("Salary.PaymentBankProfilePlaceholder"),
    currentMonthLabel: getSalaryPayoutMonthLabel({
      currentDate,
      translate,
    }),
    totalNetPayoutAmount: formatSalaryPayoutCurrency(totalNetPayoutAmount),
  };
}

export function buildSalaryPayoutSectionsViewModel({
  previewData,
  totalNetPayoutAmount,
  translate,
}: BuildSalaryPayoutSectionsViewModelParams): SalaryPayoutSectionsViewModel {
  const missingPaymentMethodRows = toExcludedWorkerRows({
    workers: previewData.noPaymentMethodWorkers,
    tagLabel: translate("Salary.PaymentMissingPaymentMethodReason"),
  });
  const missingPayoutRows = toExcludedWorkerRows({
    workers: previewData.noPayoutWorkers,
    tagLabel: translate("Salary.PaymentMissingPayoutReason"),
  });

  const excludedSections: SalaryPayoutExcludedSectionViewModel[] = [];

  if (missingPaymentMethodRows.length > 0) {
    excludedSections.push(
      buildExcludedSection({
        id: "missingPaymentMethod",
        rows: missingPaymentMethodRows,
        translate,
      }),
    );
  }

  if (missingPayoutRows.length > 0) {
    excludedSections.push(
      buildExcludedSection({
        id: "missingPayout",
        rows: missingPayoutRows,
        translate,
      }),
    );
  }

  return {
    excludedSections,
    includedSection: {
      emptyText: translate("Salary.PaymentNoIncludedWorkers"),
      rows: toIncludedWorkerRows({ workers: previewData.payoutWorkers }),
      subtitle: translate("Salary.PaymentIncludedWorkersSummary", {
        amount: formatSalaryPayoutCurrency(totalNetPayoutAmount),
      }),
      title: translate("Salary.PaymentIncludedWorkersAccordionTitle", {
        count: previewData.payoutWorkers.length,
      }),
    },
  };
}

export function resolveSalaryPayoutCreateErrorMessage(
  backendMessage: string | null,
  translate: TranslateFn,
): string {
  const translationKeyMap: Record<
    string,
    SalaryPayoutCreateErrorTranslationKey
  > = {
    "Company not found": "Salary.PaymentCreateErrorCompanyNotFound",
    "Company missing required setting, bank account type":
      "Salary.PaymentCreateErrorMissingBankAccountType",
    "Company missing required settings":
      "Salary.PaymentCreateErrorMissingRequiredSettings",
    "Company has no valid workers.": "Salary.PaymentCreateErrorNoValidWorkers",
    "Company has no payouts.": "Salary.PaymentCreateErrorNoPayouts",
    "No payment method found.": "Salary.PaymentCreateErrorNoPaymentMethod",
  };

  const translationKey =
    backendMessage == null
      ? "Alerts.SomethingWentWrong"
      : (translationKeyMap[backendMessage] ?? "Alerts.SomethingWentWrong");

  return translate(translationKey);
}
